import React, { useState, useCallback, useEffect } from 'react';
import { MessageSquare, Check, X } from 'lucide-react';
import syncService from '../../services/syncService';
import Spinner from './Spinner';

// An operator's note on one section of a report (V368 report_annotations).
//
// The Sync Report has had these since V368; the On-site Instructions report needs the same thing on its
// sections, and the two must behave identically — the same store, the same key, the same wording — or a
// note written on one report is invisible on the other. So the whole thing lives here rather than being
// copied, which is how the second copy would start drifting on its first bug fix.
//
// ONE ROW PER (task, group_key). Saving an empty note removes it. The note is included in the on-screen
// report and in whatever the page downloads, emails or prints — which is the point: it is the sentence a
// person adds to explain a grouping to whoever has to act on it.
//
// Props:
//   syncId, taskId   which report
//   groupKey             the section — must be stable across renders and reloads, or the note detaches
//   value                current text ('' when none)
//   onChange(key, text)  told after a successful save, so the page can update its own map
//   onError(message)     surfaced by the page, which owns the error banner

export function useReportAnnotations(syncId, taskId) {
    const [annotations, setAnnotations] = useState({});

    useEffect(() => {
        let cancelled = false;
        if (!syncId || !taskId) return undefined;
        (async () => {
            try {
                const a = await syncService.getReportAnnotations(syncId, taskId);
                const map = {};
                for (const [k, v] of Object.entries(a?.annotations || {})) map[k] = v?.annotation || '';
                if (!cancelled) setAnnotations(map);
            } catch (_) {
                // Best-effort: a report that renders without its notes is far better than one that does
                // not render.
            }
        })();
        return () => { cancelled = true; };
    }, [syncId, taskId]);

    const apply = useCallback((key, text) => {
        setAnnotations(prev => {
            const next = { ...prev };
            if (text) next[key] = text; else delete next[key];
            return next;
        });
    }, []);

    return [annotations, apply];
}

export default function ReportAnnotation({ syncId, taskId, groupKey, value, onChange, onError }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);

    const start = () => { setDraft(value || ''); setEditing(true); };

    const save = async () => {
        const text = draft.trim();
        try {
            setSaving(true);
            await syncService.saveReportAnnotation(syncId, taskId, groupKey, text);
            if (onChange) onChange(groupKey, text);
            setEditing(false);
            setDraft('');
        } catch (e) {
            if (onError) onError(e.response?.data?.message || e.message || 'Failed to save annotation');
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        return (
            <>
                {/* report-no-print: the button is a control, the note is content. Printing the button
                    would put "Annotate" in the middle of a document sent to a hotel. */}
                <button className="report-annotate-btn report-no-print" onClick={start}>
                    <MessageSquare size={13} /> {value ? 'Edit annotation' : 'Annotate'}
                </button>
                {value && <div className="report-annotation"><strong>Annotation:</strong> {value}</div>}
            </>
        );
    }

    return (
        <div className="report-annotate-editor report-no-print">
            <textarea
                className="report-annotate-textarea"
                rows={3}
                autoFocus
                placeholder="Add context to clarify this grouping…"
                value={draft}
                onChange={e => setDraft(e.target.value)}
            />
            <div className="report-annotate-actions">
                <button className="report-annotate-save" onClick={save} disabled={saving}>
                    {saving ? <Spinner size="sm" /> : <Check size={14} />} Save
                </button>
                <button className="report-annotate-cancel" onClick={() => setEditing(false)} disabled={saving}>
                    <X size={14} /> Cancel
                </button>
            </div>
        </div>
    );
}
